import { useEffect, useState } from "react";
import { X, DollarSign } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Iter387 · Organiser Bespoke Pricing Modal — Owner-only.
 * Applies pricing overrides to ALL festivals of the target organiser.
 * Per-festival bespoke still wins per-field.
 */
export default function OrganiserPricingModal({ organiser, open, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [existing, setExisting] = useState(null);

  useEffect(() => {
    if (!open || !organiser?.id) return;
    (async () => {
      try {
        const r = await api.get(`/admin/organiser/${organiser.id}/bespoke`);
        setExisting(r.data.bespoke || null);
        setForm(r.data.bespoke?.overrides || {});
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load organiser pricing");
      }
    })();
  }, [open, organiser?.id]);

  if (!open || !organiser) return null;

  const set = (k, v) => setForm({ ...form, [k]: v === "" ? undefined : v });

  const save = async () => {
    setBusy(true);
    try {
      const payload = {};
      for (const [k, v] of Object.entries(form)) {
        if (v === "" || v === null || v === undefined) continue;
        if (["tier_amount", "self_service_amount", "early_access_amount"].includes(k)) payload[k] = Number(v);
        else if (["player_cap", "check_in_helpers", "court_ops_helpers", "deputies",
                  "early_access_hours", "gifted_helpers"].includes(k)) payload[k] = parseInt(v);
        else payload[k] = v;
      }
      const r = await api.post(`/admin/organiser/${organiser.id}/bespoke`, payload);
      setExisting(r.data.bespoke);
      toast.success("Organiser pricing saved");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Remove organiser-wide bespoke pricing? Per-festival bespoke packs stay untouched.")) return;
    setBusy(true);
    try {
      await api.delete(`/admin/organiser/${organiser.id}/bespoke`);
      setExisting(null);
      setForm({});
      toast.success("Removed");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed");
    } finally { setBusy(false); }
  };

  const num = (k, label) => (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">{label}</div>
      <input type="number" min={0} value={form[k] ?? ""}
             onChange={(e) => set(k, e.target.value)}
             data-testid={`org-bespoke-${k}`}
             className="w-full bg-[#141414] border border-[#262626] px-2 py-2 text-white text-xs mt-1" />
    </label>
  );

  return (
    <div className="fixed inset-0 z-[95] bg-black/70 flex items-center justify-center p-4"
         onClick={() => !busy && onClose()}
         data-testid="org-pricing-modal">
      <div className="w-full max-w-2xl bg-[#0A0A0A] border-2 border-[#FFB020] p-5 max-h-[92vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="label-mono text-[#FFB020] text-[10px] flex items-center gap-1">
              <DollarSign className="w-3 h-3" /> ORGANISER-WIDE PRICING
            </div>
            <h2 className="h-display text-xl">{organiser.name || organiser.email}</h2>
            <div className="text-[10px] uppercase tracking-widest text-[#525252]">{organiser.email}</div>
          </div>
          <button type="button" onClick={() => !busy && onClose()}
                  className="text-[#A3A3A3] hover:text-white" data-testid="org-pricing-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="text-xs text-[#A3A3A3] mb-4">
          Applies to every festival this organiser runs. Per-festival bespoke packs still win per-field.
          Leave a field blank to fall through to the per-festival or global default.
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block sm:col-span-2">
            <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">Pack name (optional)</div>
            <input value={form.pack_name ?? ""} onChange={(e) => set("pack_name", e.target.value)}
                   data-testid="org-bespoke-pack_name"
                   className="w-full bg-[#141414] border border-[#262626] px-2 py-2 text-white text-xs mt-1" />
          </label>
          {num("tier_amount", "Base tier amount")}
          {num("self_service_amount", "Self-service bolt-on")}
          {num("player_cap", "Player cap")}
          {num("check_in_helpers", "Check-in helpers included")}
          {num("court_ops_helpers", "Court-ops helpers included")}
          {num("deputies", "Deputies included")}
          {num("early_access_hours", "Early access hours")}
          {num("early_access_amount", "Early access charge")}
          {num("gifted_helpers", "Gifted helpers")}
          <label className="block sm:col-span-2">
            <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">Note (visible on audits)</div>
            <textarea value={form.note ?? ""} onChange={(e) => set("note", e.target.value)}
                      data-testid="org-bespoke-note"
                      rows={2}
                      className="w-full bg-[#141414] border border-[#262626] px-2 py-2 text-white text-xs mt-1" />
          </label>
        </div>

        <div className="flex justify-between mt-4 gap-2">
          <button type="button" onClick={remove} disabled={busy || !existing}
                  data-testid="org-pricing-remove"
                  className="px-4 py-2 text-xs uppercase tracking-widest border border-[#FF4500] text-[#FF4500] hover:bg-[#FF4500] hover:text-black disabled:opacity-40">
            Remove override
          </button>
          <button type="button" onClick={save} disabled={busy}
                  data-testid="org-pricing-save"
                  className="px-4 py-2 text-xs uppercase tracking-widest font-bold bg-[#FFB020] text-black hover:bg-white disabled:opacity-40">
            {busy ? "Saving…" : "Save organiser pricing"}
          </button>
        </div>
      </div>
    </div>
  );
}
